import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  Signal,
  viewChild,
} from '@angular/core';
import * as L from 'leaflet';
import { TrainingSectionType } from '../../models/training-section.model';
import { decodePolyline } from '../../helpers/polyline-decode.helpers';
import { getTrainingSectionColor } from '../../helpers/training-section-color.helpers';
import { LatLngLiteral } from 'leaflet';
import { LeafletService } from '../../services/leaflet-service';
import { MapModeType } from '../../models/map-mode.model';
import { WebSocketService } from '../../services/websocket-service';
import { WSPlayer } from '../../models/WS-player.model';
import { PlayerMarkerType } from '../../models/player-marker.model';
import {
  SectionPolyline,
  splitSectionsPolylines,
  SplitSectionsPolylines,
} from '../../helpers/leaflet.helpers';
import { WSBroadcastDataMessage } from '../../models/WSMessages/WS-broadcast-data-message.model';
import { WSUser } from '../../models/WS-user.model';
import { concatCoordinates } from '../../helpers/concat-coordinates.helpers';

@Component({
  selector: 'app-map',
  imports: [],
  template: `<div #mapContainer id="map"></div>`,
  styleUrl: './map.scss',
})
export class Map {
  mapContainer: Signal<ElementRef<HTMLDivElement>> = viewChild.required('mapContainer', {
    read: ElementRef<HTMLDivElement>,
  });
  private readonly leafletService: LeafletService = inject(LeafletService);
  private readonly webSocketService: WebSocketService = inject(WebSocketService);

  readonly trainingSections: InputSignal<TrainingSectionType[] | undefined> = input.required<
    TrainingSectionType[] | undefined
  >();

  private readonly mapType: Signal<MapModeType> = computed(
    () => this.webSocketService.config()?.mapType ?? 'plan',
  );
  private readonly trainingsSectionsPolylines: Signal<SectionPolyline[]> = computed<
    SectionPolyline[]
  >(() => {
    const sections: TrainingSectionType[] | undefined = this.trainingSections();
    if (!sections) return [];

    return sections.map((section: TrainingSectionType) => ({
      coordinates: section.polyline ? decodePolyline(section.polyline) : concatCoordinates(section),
      color: getTrainingSectionColor(section.slope),
    }));
  });

  private readonly sectionsPolylines: Signal<SplitSectionsPolylines> = computed(() => {
    const trainingsSections: SectionPolyline[] = this.trainingsSectionsPolylines();
    const broadcastCoordinates: [number, number] | undefined =
      this.webSocketService.broadcastData()?.data?.coordinates;

    return splitSectionsPolylines(trainingsSections, broadcastCoordinates);
  });

  private readonly backgroundPolyline: Signal<L.LatLngExpression[]> = computed(() =>
    this.sectionsPolylines().remaining.flatMap(
      (polylines: SectionPolyline) => polylines.coordinates,
    ),
  );

  private readonly fullCoordinates: Signal<L.LatLngExpression[]> = computed(() =>
    this.trainingsSectionsPolylines().flatMap(
      (polylines: SectionPolyline) => polylines.coordinates,
    ),
  );

  private readonly startCoordinate: Signal<L.LatLngExpression> = computed(
    () => this.sectionsPolylines().travelled[0]?.coordinates[0] || this.backgroundPolyline()[0],
  );

  private readonly endCoordinate: Signal<L.LatLngExpression> = computed(
    () => this.backgroundPolyline().at(-1)!,
  );

  private coordinatesToSync: Signal<L.LatLngExpression[]> = computed(() => {
    const broadcastCoord: [number, number] | undefined =
      this.webSocketService.broadcastData()?.data?.coordinates;
    return this.webSocketService.config()?.map === 'center' && broadcastCoord
      ? [broadcastCoord as L.LatLngExpression]
      : this.fullCoordinates();
  });

  private map: L.Map | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private refreshFrameId: number | null = null;
  private baseTileLayers: L.TileLayer[] = [];
  private sectionLayers: L.Layer[] = [];
  private readonly playersMarkers: PlayerMarkerType[] = [];
  private readonly displayedPlayerMarkers: globalThis.Map<string, L.Marker> = new globalThis.Map<
    string,
    L.Marker
  >();
  private readonly displayedClusterMarkers: globalThis.Map<string, L.Marker> = new globalThis.Map<
    string,
    L.Marker
  >();

  constructor() {
    effect(() => {
      this.sectionsPolylines();
      this.backgroundPolyline();
      this.startCoordinate();
      this.endCoordinate();
      this.refreshSectionLayers();
    });

    effect(() => {
      this.mapType();
      this.addBaseTileLayers();
    });

    effect(() => {
      this.webSocketService.broadcastData();
      this.webSocketService.config();
      this.setPlayersMarkers();
    });
  }

  private addStartAndEndMarkers(): void {
    const startMarkerLayer: L.Marker = this.leafletService.createMarker(
      this.startCoordinate() as LatLngLiteral,
      { markerUrl: 'images/map_marker_start.svg' },
    );
    const endMarkerLayer: L.Marker = this.leafletService.createMarker(
      this.endCoordinate() as LatLngLiteral,
      { markerUrl: 'images/map_marker_end.svg' },
    );

    if (startMarkerLayer && endMarkerLayer) {
      this.addSectionLayer(startMarkerLayer);
      this.addSectionLayer(endMarkerLayer);
    }
  }

  private addSectionLayer(leafletElement: L.Polyline | L.Marker): void {
    this.sectionLayers.push(leafletElement);
    this.map?.addLayer(leafletElement);
  }

  private clearSectionLayers(): void {
    if (!this.map) return;

    this.sectionLayers.forEach((layer: L.Layer) => this.map!.removeLayer(layer));
    this.sectionLayers = [];
  }

  private refreshSectionLayers(): void {
    if (!this.map) return;

    this.clearSectionLayers();

    if (this.backgroundPolyline().length > 0) {
      const backgroundPolyline: L.Polyline = this.leafletService.createPolyline(
        this.backgroundPolyline(),
        '#191A1A',
        9,
      );
      this.addSectionLayer(backgroundPolyline);
      this.addStartAndEndMarkers();
    }

    this.sectionsPolylines().travelled.forEach((trainingSectionInfo: SectionPolyline) => {
      const polyline: L.Polyline = this.leafletService.createPolyline(
        trainingSectionInfo.coordinates,
        trainingSectionInfo.color,
      );
      this.addSectionLayer(polyline);
    });

    this.sectionsPolylines().remaining.forEach((trainingSectionInfo: SectionPolyline) => {
      const polyline: L.Polyline = this.leafletService.createPolyline(
        trainingSectionInfo.coordinates,
        trainingSectionInfo.color,
      );
      this.addSectionLayer(polyline);
    });

    if (this.sectionLayers[0]) {
      this.refreshFrameId = this.leafletService.scheduleViewportSyncToPolyline(
        this.map,
        this.coordinatesToSync(),
        this.refreshFrameId,
        () => {
          this.refreshFrameId = null;
        },
      );
    }
  }

  private refreshDisplayedPlayersLayers(): void {
    if (!this.map) return;

    this.leafletService.syncDisplayedPlayersLayers({
      map: this.map,
      playersMarkers: this.playersMarkers,
      displayedPlayerMarkers: this.displayedPlayerMarkers,
      displayedClusterMarkers: this.displayedClusterMarkers,
    });
  }

  private setPlayersMarkers(): void {
    const broadcastPlayers: WSPlayer[] = this.webSocketService.broadcastData()?.players ?? [];
    const user: WSUser | undefined = this.webSocketService.client();
    const clientData: WSBroadcastDataMessage['data'] | undefined =
      this.webSocketService.broadcastData()?.data;

    broadcastPlayers.forEach((player: WSPlayer) => {
      if (player.user.username === user?.username) {
        return;
      }

      const hasValidCoordinates: boolean = player.data.coordinates?.length === 2;
      let playerMarker: PlayerMarkerType | undefined = this.playersMarkers.find(
        (pm: PlayerMarkerType) => pm.user.username === player.user.username,
      );

      if (playerMarker && hasValidCoordinates) {
        playerMarker.coordinates = L.latLng(player.data.coordinates[0], player.data.coordinates[1]);
        playerMarker.marker.setLatLng(playerMarker.coordinates);
        return;
      }

      if (!playerMarker && hasValidCoordinates) {
        playerMarker = this.addPlayerMarker(player);
      }
    });

    if (user && clientData?.coordinates?.length === 2) {
      let clientMarker: PlayerMarkerType | undefined = this.playersMarkers.find(
        (pm: PlayerMarkerType) => pm.user.username === user.username,
      );

      const clientCoordinates: L.LatLng = L.latLng(
        clientData.coordinates[0],
        clientData.coordinates[1],
      );

      if (clientMarker) {
        clientMarker.coordinates = clientCoordinates;
        clientMarker.marker.setLatLng(clientCoordinates);
        return;
      }

      clientMarker = this.addPlayerMarker({
        user,
        data: clientData,
        finalTime: undefined,
        isSlipstreamActive: false,
      });
    }

    this.playersMarkers
      .filter(
        (playerMarker: PlayerMarkerType) =>
          playerMarker.user.username !== user?.username &&
          !broadcastPlayers.some(
            (player: WSPlayer) => player.user.username === playerMarker.user.username,
          ),
      )
      .forEach((playerMarker: PlayerMarkerType) => {
        this.leafletService.removePlayerFromDisplayedLayers(
          this.map,
          this.displayedPlayerMarkers,
          this.displayedClusterMarkers,
          playerMarker.user.username,
        );

        const playerMarkerIndex: number = this.playersMarkers.findIndex(
          (pm: PlayerMarkerType) => pm.user.username === playerMarker.user.username,
        );

        if (playerMarkerIndex !== -1) {
          this.playersMarkers.splice(playerMarkerIndex, 1);
        }
      });

    this.refreshDisplayedPlayersLayers();
  }

  private addPlayerMarker(player: WSPlayer): PlayerMarkerType | undefined {
    const isClient: boolean = player.user.username === this.webSocketService.client()?.username;
    const playerMarker: PlayerMarkerType = this.leafletService.createPlayerMarker(player, isClient);

    this.playersMarkers.push(playerMarker);
    return playerMarker;
  }

  private addBaseTileLayers(): void {
    const type: MapModeType = this.mapType();
    if (!this.map) return;

    this.baseTileLayers = this.leafletService.replaceTileLayers(
      this.map,
      this.baseTileLayers,
      type,
    );
  }

  ngAfterViewInit(): void {
    this.map = L.map(this.mapContainer().nativeElement, this.leafletService.INITIAL_MAP_OPTION);
    this.addBaseTileLayers();
    this.refreshSectionLayers();
    this.setPlayersMarkers();
    this.map.on('zoomend moveend', () => this.refreshDisplayedPlayersLayers());
    this.resizeObserver = new ResizeObserver(() => {
      this.refreshFrameId = this.leafletService.scheduleViewportSyncToPolyline(
        this.map,
        this.coordinatesToSync(),
        this.refreshFrameId,
        () => {
          this.refreshFrameId = null;
        },
      );
      this.refreshDisplayedPlayersLayers();
    });
    this.resizeObserver.observe(this.mapContainer().nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.clearSectionLayers();
  }
}
